import { createContext, useContext, useState, useCallback, useRef } from 'react';
import SignInModal from '../components/modals/SignInModal';
import { useDiffitUserContext } from './DiffitUserContext';

const SignInModalContext = createContext(null);

/**
 * The app's single SignInModal instance, mounted once in Base so every route shares it.
 *
 * It used to be per-surface state: the sidebar, the mobile drawer's second copy of the
 * sidebar, the packet modal stack and Base's ?signup= handler each rendered their own
 * modal, so four were mounted at once on a signed-out packet page. They are not free
 * while closed — each one consumes `to_email` / `sharer_id` off the URL on mount and
 * deletes them, and since useSearchParams hands every instance the SAME mutable
 * URLSearchParams, the first effect to run took the values and the rest read null. Which
 * instance won was mount order, not which one the visitor opened, so an invite link's
 * email prefill and its sharer attribution went missing whenever those disagreed.
 * (utils/signInPrefill closes that race at the source, for the sign-in walls in App,
 * promo, subscription and JoinOrg that still render their own modal.)
 */
export const SignInModalProvider = ({ children }) => {
  const { isLDidentified } = useDiffitUserContext();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState({});
  const onCloseRef = useRef(null);

  const showSignInModal = useCallback((opts) => {
    onCloseRef.current = opts?.onClose || null;
    setOptions(opts || {});
    setOpen(true);
  }, []);

  // Wrap setOpen so a dismissal (setOpen(false)) fires the opener's onClose once.
  // Successful sign-up reloads the page instead of calling setOpen(false), so
  // onClose only runs when the user closes the modal without authenticating.
  const handleSetOpen = useCallback((next) => {
    setOpen(next);
    if (!next && onCloseRef.current) {
      const cb = onCloseRef.current;
      onCloseRef.current = null;
      cb();
    }
  }, []);

  // waitForLDIdentify holds the modal back until identify resolves, so the copy can't
  // paint one experiment arm and then swap: the packet surfaces auto-open on Clerk's
  // isLoaded, which precedes identify. Only they ask for it — for everyone else a
  // sign-in click has to open something now, and resolveSignInCopy already serves
  // control copy while unidentified.
  const held = options.waitForLDIdentify && !isLDidentified;

  return (
    <SignInModalContext.Provider value={showSignInModal}>
      <SignInModal
        open={open && !held}
        setOpen={handleSetOpen}
        source={options.source || ''}
        referrerGivenName={options.referrerGivenName || ''}
        // Opt-in mandatory mode: pass { dismissible: false } to remove the close
        // affordances (X, backdrop click, ESC). Defaults to dismissible.
        dismissible={options.dismissible !== false}
        // Optional copy overrides for the ENTER_EMAIL screen (else modal defaults).
        headline={options.headline || ''}
        subtitle={options.subtitle || ''}
      />
      {children}
    </SignInModalContext.Provider>
  );
};

/**
 * Returns a function that opens the sign-in modal. Call from any component under Base.
 *
 * Options: `source` (SIGN_IN_SOURCE, scopes the copy experiment), `referrerGivenName` (the
 * homepage referral welcome's already-resolved referrer), `headline` / `subtitle` overrides,
 * `dismissible: false`, `waitForLDIdentify`, and `onClose` to run a callback when the user
 * dismisses without signing in.
 * @returns {Function} showSignInModal - Opens the sign-in modal.
 */
export const useSignInModal = () => {
  const showSignInModal = useContext(SignInModalContext);
  if (!showSignInModal) {
    throw new Error('useSignInModal must be used within a SignInModalProvider');
  }
  return showSignInModal;
};
